import React from 'react';
import { useTranslation } from 'react-i18next';
import WifiIcon from '@mui/icons-material/Wifi';
import PoolIcon from '@mui/icons-material/Pool';
import SpaIcon from '@mui/icons-material/Spa';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import FreeBreakfastIcon from '@mui/icons-material/FreeBreakfast';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import LocalLaundryServiceIcon from '@mui/icons-material/LocalLaundryService';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import { getHotelTypeLabel } from '../constants/hotelMasterData';
import type { RatePolicyMode } from '../hooks/useHotelBooking';
import type { HotelDetailData } from '../types/hotels.presentation';
import { filterRoomOptionsByPolicy } from '../utils/filterRoomOptionsByPolicy';
import { HotelDescription } from './HotelDescription';
import { RoomOptionCard } from './RoomOptionCard';

const AMENITY_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  'wi-fi': WifiIcon,
  wifi: WifiIcon,
  piscina: PoolIcon,
  pool: PoolIcon,
  spa: SpaIcon,
  gimnasio: FitnessCenterIcon,
  fitness: FitnessCenterIcon,
  gym: FitnessCenterIcon,
  restaurante: RestaurantIcon,
  restaurant: RestaurantIcon,
  bar: LocalBarIcon,
  estacionamiento: LocalParkingIcon,
  parking: LocalParkingIcon,
  'traslado aeropuerto': AirportShuttleIcon,
  'airport shuttle': AirportShuttleIcon,
  desayuno: FreeBreakfastIcon,
  breakfast: FreeBreakfastIcon,
  'centro de negocios': BusinessCenterIcon,
  'business center': BusinessCenterIcon,
  'salas de reuniones': MeetingRoomIcon,
  'meeting rooms': MeetingRoomIcon,
  'servicio a la habitación': RoomServiceIcon,
  'room service': RoomServiceIcon,
  lavandería: LocalLaundryServiceIcon,
  lavanderia: LocalLaundryServiceIcon,
  laundry: LocalLaundryServiceIcon,
  sauna: WhatshotIcon,
};

function amenityIconComponent(
  label: string,
): React.ComponentType<{ className?: string }> | undefined {
  return AMENITY_ICON_MAP[label.toLowerCase().trim()];
}

interface HotelDetailContentProps {
  hotel: HotelDetailData;
  canBook?: boolean;
  onBook?: (rateKey: string, rateName?: string) => void;
  ratePolicyModes?: Record<string, RatePolicyMode>;
  policyQuotesReady?: boolean;
}

export function HotelDetailContent({
  hotel,
  canBook,
  onBook,
  ratePolicyModes = {},
  policyQuotesReady = false,
}: HotelDetailContentProps) {
  const { t, i18n } = useTranslation();

  const visibleRoomOptions = filterRoomOptionsByPolicy(hotel.roomOptions, ratePolicyModes, {
    // Keep all quoted rates visible; blocked/unavailable still offer Consultar.
    hideUntilQuoted: false,
  });
  const hasRoomOptions = visibleRoomOptions.length > 0;
  const accommodationLabel =
    hotel.hotelTypeId != null ? getHotelTypeLabel(hotel.hotelTypeId, i18n.language) : undefined;

  return (
    <>
      <div className='mt-6 space-y-4'>
        <section>
          <h3 className='text-sm font-semibold uppercase tracking-wide text-gray-700'>
            {t('hotels.detail.description')}
          </h3>
          <HotelDescription description={hotel.description} />
        </section>

        {hotel.amenities?.length ? (
          <section>
            <h3 className='text-sm font-semibold uppercase tracking-wide text-gray-700'>
              {t('hotels.card.amenities')}
            </h3>
            <div className='mt-2 flex flex-wrap gap-1.5'>
              {hotel.amenities.map((amenity) => {
                const Icon = amenityIconComponent(amenity);
                return (
                  <span
                    className='inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset bg-gray-50 text-gray-700 ring-gray-200'
                    key={amenity}
                  >
                    {Icon ? <Icon aria-hidden='true' className='mr-1 h-3.5 w-3.5' /> : null}
                    {amenity}
                  </span>
                );
              })}
            </div>
          </section>
        ) : null}
      </div>

      {hasRoomOptions ? (
        <section className='mt-6 border-t border-gray-100 pt-6'>
          <h3 className='text-sm font-semibold uppercase tracking-wide text-gray-700'>
            {t('hotels.detail.roomOptions')}
          </h3>
          <p className='mt-1 text-sm text-gray-500'>{t('hotels.detail.roomOptionsSubtitle')}</p>

          <div className='mt-4 space-y-3'>
            {visibleRoomOptions.map((option) => (
              <RoomOptionCard
                accommodationLabel={accommodationLabel}
                canBook={canBook}
                key={option.bestRateKey}
                onBook={onBook}
                option={option}
                policyQuotesReady={policyQuotesReady}
                ratePolicyModes={ratePolicyModes}
              />
            ))}
          </div>
        </section>
      ) : (
        <section className='mt-6 border-t border-gray-100 pt-6'>
          <h3 className='text-sm font-semibold uppercase tracking-wide text-gray-700'>
            {t('hotels.detail.roomOptions')}
          </h3>
          <p className='mt-1 text-sm italic text-gray-500'>{t('hotels.detail.noRoomsAvailable')}</p>
        </section>
      )}
    </>
  );
}
